import React, { useState, useEffect } from "react";
import { Grid, Skeleton } from "@mui/material";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import Button from "@mui/material/Button";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useHistory } from "react-router-dom";
import RoutesName from "routes/routesName";
import DeleteScene from "./DeleteScene";
import cmrLogo from "ui/resources/logos/cmr_white.png";

type Scene = {
    id: string;
    sceneName: string;
    displayName: string;
    surgeon?: string | null;
    hospital?: string | null;
    country?: string | null;
    thumbnail?: string | null;
    global: boolean;
};

type Props = {
    scene?: Scene;
    onDeleted?: () => void;
    displayName?: boolean;
};

const SetupCard: React.FC<Props> = ({ scene, onDeleted, displayName }) => {
    const [thumbnailSrc, setThumbnailSrc] = useState<string | undefined>();
    const router = useHistory();
    const handleLoad = (): void => {
        if (!scene) return;
        router.push({
            pathname: RoutesName.modelPage,
            state: { sceneKey: scene.id, isGlobal: scene.global },
        });
    };

    useEffect(() => {
        if (!scene?.thumbnail) return;

        const thumbnail = scene.thumbnail as any;

        if (thumbnail?.startsWith("\\x")) {
            const hex = thumbnail.substring(2);

            let binary = "";

            for (let i = 0; i < hex.length; i += 2) {
                binary += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
            }

            const base64 = btoa(binary);

            setThumbnailSrc(`data:image/png;base64,${base64}`);
            return;
        }

        if (typeof thumbnail === "string") {
            setThumbnailSrc(
                thumbnail.startsWith("data:") ? thumbnail : `data:image/png;base64,${thumbnail}`
            );
        }
    }, [scene?.thumbnail]);

    return (
        <Grid item xs={12} sm={6} md={4}>
            {!scene ? (
                <Card className="presetCard">
                    <Skeleton variant="rectangular" className="presetCard" />
                    <CardContent className="cardContent">
                        <Grid
                            container
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ padding: "6px 0" }}
                        >
                            <Grid item xs={8}>
                                <React.Fragment>
                                    <Skeleton
                                        animation="wave"
                                        height={20}
                                        style={{ marginRight: 10, width: "60%" }}
                                    />
                                </React.Fragment>
                            </Grid>
                            <Grid item xs={4}>
                                <div
                                    style={{
                                        flexDirection: "row",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Skeleton
                                        animation="wave"
                                        variant="circular"
                                        sx={{ width: 40, height: 40 }}
                                    />
                                    <Skeleton
                                        animation="wave"
                                        height={20}
                                        style={{ marginLeft: 20, width: 40 }}
                                    />
                                </div>
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            ) : (
                <div style={{ position: "relative", height: "100%" }}>
                    {onDeleted && (
                        <DeleteScene
                            sceneKey={scene.id}
                            isGlobal={scene.global}
                            onDeleted={() => onDeleted?.()}
                        />
                    )}
                    <Card className="presetCard" onClick={handleLoad}>
                        {scene.global && (
                            <div className="ribbon">
                                <img src={cmrLogo} alt="CMR" />
                            </div>
                        )}
                        {
                            <CardMedia
                                component="img"
                                height="250"
                                data-src={thumbnailSrc}
                                className="lazyload"
                                alt="preset item"
                            />
                        }
                        <CardContent className="cardContent">
                            <div style={{ display: "flex", flexDirection: "column", flex: 1 }}>
                                <h3 style={{ marginTop: "0.5rem" }}>{scene.sceneName}</h3>
                                {displayName && (
                                    <>
                                        {scene.surgeon && <p>Surgeon: {scene.surgeon}</p>}
                                        {scene.hospital && <p>Hospital: {scene.hospital}</p>}
                                        {scene.country && <p>Country: {scene.country}</p>}
                                    </>
                                )}
                            </div>

                            <div
                                style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    borderTop: "0.5px solid #fff",
                                    marginTop: "0.5rem",
                                }}
                            >
                                <p>Created by: {scene.displayName}</p>
                                <Button startIcon={<ArrowForwardIosIcon className="iconArrow" />}>
                                    Load
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </Grid>
    );
};

export default SetupCard;
